/**
 * CsvImportModal — Import CSV de prospects avec mapping de champs et lancement d'audits en lot.
 *
 * Étapes :
 *   1. Upload  → détection séparateur + encodage
 *   2. Mapping → correspondance colonnes CSV ↔ champs cibles
 *   3. Options → taille de lot, lancer audits ou importer seulement
 */
import React, { useState, useRef, useCallback } from 'react';
import {
    Upload, ChevronRight, ChevronLeft, Check, AlertTriangle,
    Loader2, FileText, X, Info, RefreshCw
} from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import Tooltip from './Tooltip';
import axiosService from '../services/axiosService';
import { API_BASE_URL as API_BASE } from '../config/api';

// Champs cibles reconnus + leurs alias automatiques (insensible à la casse, espaces/tirets ignorés)
const TARGET_FIELDS = [
    {
        key: 'url',
        label: 'Site internet (URL)',
        required: true,
        aliases: ['url', 'site', 'website', 'domaine', 'domain', 'lien', 'link', 'adresse', 'siteinternet', 'siteinternet'],
    },
    {
        key: 'email',
        label: 'Email',
        required: false,
        aliases: ['email', 'mail', 'e-mail', 'courriel', 'emailaddress', 'adressemail'],
    },
    {
        key: 'first_name',
        label: 'Prénom',
        required: false,
        aliases: ['first_name', 'firstname', 'prenom', 'prénom', 'forename', 'given_name'],
    },
    {
        key: 'last_name',
        label: 'Nom',
        required: false,
        aliases: ['last_name', 'lastname', 'nom', 'surname', 'family_name', 'name'],
    },
    {
        key: 'phone',
        label: 'Téléphone',
        required: false,
        aliases: ['phone', 'tel', 'téléphone', 'telephone', 'mobile', 'portable', 'gsm', 'phonenumber'],
    },
    {
        key: 'company',
        label: 'Entreprise',
        required: false,
        aliases: ['company', 'entreprise', 'societe', 'société', 'organization', 'organisation', 'org', 'structure'],
    },
];

const BATCH_SIZES = [
    { value: 5, label: '5 à la fois' },
    { value: 10, label: '10 à la fois' },
    { value: 20, label: '20 à la fois' },
    { value: 9999, label: 'Tous en même temps' },
];

// ── Helpers CSV ────────────────────────────────────────────────────────────────

function detectSeparator(text) {
    const line = text.split('\n')[0] || '';
    const counts = { ';': 0, ',': 0, '\t': 0 };
    for (const ch of line) if (ch in counts) counts[ch]++;
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

function parseCsv(text, separator) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const parseLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
                else inQuotes = !inQuotes;
            } else if (ch === separator && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += ch;
            }
        }
        result.push(current.trim());
        return result;
    };

    const headers = parseLine(lines[0]);
    const rows = lines.slice(1).map(l => {
        const cells = parseLine(l);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
        return obj;
    }).filter(row => Object.values(row).some(v => v.trim()));

    return { headers, rows };
}

function autoDetectMapping(headers) {
    const normalize = (s) => (s || '').toLowerCase().replace(/[\s\-_]/g, '');
    const mapping = {};
    for (const header of headers) {
        const norm = normalize(header);
        for (const field of TARGET_FIELDS) {
            if (field.aliases.some(a => normalize(a) === norm)) {
                if (!mapping[field.key]) mapping[field.key] = header;
                break;
            }
        }
    }
    return mapping;
}

async function readFileAsText(file) {
    // Tente UTF-8, puis Latin-1 si erreur de décodage
    const tryDecode = (buffer, encoding) => {
        try {
            const decoder = new TextDecoder(encoding, { fatal: true });
            return decoder.decode(buffer);
        } catch {
            return null;
        }
    };
    const buffer = await file.arrayBuffer();
    return tryDecode(buffer, 'utf-8') || tryDecode(buffer, 'windows-1252') || new TextDecoder('latin1').decode(buffer);
}

// ── Composants UI internes ─────────────────────────────────────────────────────

function StepIndicator({ step }) {
    const steps = ['Upload', 'Mapping', 'Options'];
    return (
        <div className="flex items-center gap-2 mb-6">
            {steps.map((label, idx) => {
                const i = idx + 1;
                const active = step === i;
                const done = step > i;
                return (
                    <React.Fragment key={label}>
                        <div className="flex items-center gap-1.5">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${done ? 'bg-accent border-accent text-white' : active ? 'border-accent text-accent bg-accent/10' : 'border-blue-gray-200 text-blue-gray-400'}`}>
                                {done ? <Check size={12} /> : i}
                            </div>
                            <span className={`text-xs font-medium hidden sm:inline ${active ? 'text-accent' : done ? 'text-blue-gray-600' : 'text-blue-gray-400'}`}>{label}</span>
                        </div>
                        {idx < steps.length - 1 && <div className={`flex-1 h-px transition-all ${done ? 'bg-accent' : 'bg-blue-gray-200'}`} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function CsvImportModal({ open, onClose, onImportDone }) {
    const [step, setStep] = useState(1);
    const [dragOver, setDragOver] = useState(false);
    const [parseError, setParseError] = useState(null);
    const [listName, setListName] = useState('');       // Nom de l'onglet/liste
    const [listNameError, setListNameError] = useState(false);
    const [csvData, setCsvData] = useState(null); // { headers, rows, separator }
    const [mapping, setMapping] = useState({});   // { url: "col_header", email: "col_email", ... }
    const [runAudit, setRunAudit] = useState(true);
    const [batchSize, setBatchSize] = useState(10);
    const [dedupDays, setDedupDays] = useState(0);
    const [utmSource, setUtmSource] = useState('');
    const [utmMedium, setUtmMedium] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    const reset = useCallback(() => {
        setStep(1);
        setDragOver(false);
        setParseError(null);
        setListName('');
        setListNameError(false);
        setCsvData(null);
        setMapping({});
        setRunAudit(true);
        setBatchSize(10);
        setDedupDays(0);
        setUtmSource('');
        setUtmMedium('');
        setImporting(false);
        setImportResult(null);
    }, []);

    const handleClose = () => { reset(); onClose(); };

    // ── Parsing fichier ──────────────────────────────────────────────────────

    const handleFile = useCallback(async (file) => {
        setParseError(null);
        // Valider le nom de liste avant de continuer
        if (!listName.trim()) {
            setListNameError(true);
            return;
        }
        setListNameError(false);
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setParseError('Seuls les fichiers .csv sont acceptés.');
            return;
        }
        try {
            const text = await readFileAsText(file);
            const sep = detectSeparator(text);
            const { headers, rows } = parseCsv(text, sep);
            if (headers.length === 0) { setParseError('Fichier vide ou illisible.'); return; }
            if (rows.length === 0) { setParseError('Aucune ligne de données trouvée.'); return; }
            const detected = autoDetectMapping(headers);
            setCsvData({ headers, rows, separator: sep, fileName: file.name });
            setMapping(detected);
            setStep(2);
        } catch (e) {
            setParseError(`Erreur de lecture : ${e.message}`);
        }
    }, [listName]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const onFileChange = (e) => { if (e.target.files[0]) handleFile(e.target.files[0]); };

    // ── Validation mapping ───────────────────────────────────────────────────

    const urlMapped = !!mapping.url;
    const validRows = csvData ? csvData.rows.filter(row => {
        const urlVal = (row[mapping.url] || '').trim();
        return urlVal.length > 0;
    }) : [];
    const invalidRows = csvData ? csvData.rows.length - validRows.length : 0;

    // ── Import ───────────────────────────────────────────────────────────────

    const handleImport = async () => {
        if (!csvData || !urlMapped) return;
        setImporting(true);
        setImportResult(null);
        try {
            const prospects = validRows.map(row => ({
                url: (row[mapping.url] || '').trim(),
                email: mapping.email ? (row[mapping.email] || '').trim() : '',
                first_name: mapping.first_name ? (row[mapping.first_name] || '').trim() : '',
                last_name: mapping.last_name ? (row[mapping.last_name] || '').trim() : '',
                phone: mapping.phone ? (row[mapping.phone] || '').trim() : '',
                company: mapping.company ? (row[mapping.company] || '').trim() : '',
                original_row: row,
            }));

            const res = await axiosService.post(`${API_BASE}/api/public/admin/prospects/batch-audit`, {
                prospects,
                run_audit: runAudit,
                source: 'csv_import',
                dedup_days: dedupDays,
                list_name: listName.trim(),
                utm_source: utmSource.trim() || undefined,
                utm_medium: utmMedium.trim() || undefined,
                // Schéma d'origine : permet de ré-exporter le CSV avec les mêmes colonnes
                csv_original_headers: csvData.headers,
                csv_mapping: mapping,  // { url: "Website", email: "E-mail", ... }
            });
            setImportResult(res.data);
            if (onImportDone) onImportDone(res.data.prospect_list_id);
        } catch (e) {
            setImportResult({ error: e.response?.data?.detail || e.message || 'Erreur inconnue' });
        } finally {
            setImporting(false);
        }
    };

    // ── Rendu ────────────────────────────────────────────────────────────────

    return (
        <Modal open={open} onClose={handleClose} title="Importer des prospects depuis un CSV" size="xl">
            <StepIndicator step={step} />

            {/* ── Étape 1 : Upload ── */}
            {step === 1 && (
                <div className="flex flex-col gap-4">
                    {/* Nom de la liste — obligatoire */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-blue-gray-700 flex items-center gap-1">
                            Nom de la liste <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Ex : Clients e-commerce, Prospects Paris Mai 2026…"
                            value={listName}
                            onChange={e => { setListName(e.target.value); if (e.target.value.trim()) setListNameError(false); }}
                            className={`w-full px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white transition ${listNameError ? 'border-red-400 bg-red-50' : 'border-blue-gray-200'}`}
                            maxLength={100}
                        />
                        {listNameError && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertTriangle size={12} /> Ce champ est obligatoire — il deviendra le nom de l'onglet.
                            </p>
                        )}
                        <p className="text-xs text-blue-gray-400">Ce nom apparaîtra comme onglet dans la liste des prospects.</p>
                    </div>

                    <div
                        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${dragOver ? 'border-accent bg-accent/5' : 'border-blue-gray-200 hover:border-accent/50 hover:bg-blue-gray-50'}`}
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Upload size={32} className="mx-auto mb-3 text-blue-gray-300" />
                        <p className="text-sm font-medium text-blue-gray-600">Glissez votre fichier CSV ici</p>
                        <p className="text-xs text-blue-gray-400 mt-1">ou cliquez pour parcourir</p>
                        <p className="text-xs text-blue-gray-300 mt-3">Séparateurs supportés : virgule, point-virgule, tabulation · Encodages : UTF-8, Latin-1</p>
                        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={onFileChange} />
                    </div>
                    {parseError && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            {parseError}
                        </div>
                    )}
                    <div className="bg-blue-gray-50 rounded-lg px-4 py-3 text-xs text-blue-gray-500 flex items-start gap-2">
                        <Info size={13} className="flex-shrink-0 mt-0.5 text-accent" />
                        <span>
                            Le fichier doit contenir au minimum une colonne <strong>URL / Site</strong>. Les colonnes email, prénom, nom, téléphone et entreprise sont optionnelles et seront détectées automatiquement.
                        </span>
                    </div>
                </div>
            )}

            {/* ── Étape 2 : Mapping ── */}
            {step === 2 && csvData && (
                <div className="flex flex-col gap-5">
                    <div className="flex items-center gap-2 text-sm text-blue-gray-600">
                        <FileText size={14} className="text-accent" />
                        <span className="font-medium">{csvData.fileName}</span>
                        <span className="text-blue-gray-400">— {csvData.rows.length} ligne{csvData.rows.length > 1 ? 's' : ''} · séparateur : <code className="bg-blue-gray-100 px-1 rounded text-xs">{csvData.separator === '\t' ? 'tab' : csvData.separator}</code></span>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-blue-gray-700 mb-3">Correspondance des colonnes</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {TARGET_FIELDS.map(field => (
                                <div key={field.key} className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-blue-gray-600 flex items-center gap-1">
                                        {field.label}
                                        {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <select
                                        className={`px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white ${field.required && !mapping[field.key] ? 'border-red-300 bg-red-50' : 'border-blue-gray-200'}`}
                                        value={mapping[field.key] || ''}
                                        onChange={e => setMapping(m => ({ ...m, [field.key]: e.target.value || undefined }))}
                                    >
                                        <option value="">— Ignorer —</option>
                                        {csvData.headers.map(h => (
                                            <option key={h} value={h}>{h}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Prévisualisation */}
                    <div>
                        <p className="text-xs font-medium text-blue-gray-500 uppercase tracking-wider mb-2">Aperçu (5 premières lignes)</p>
                        <div className="overflow-x-auto rounded-lg border border-blue-gray-100">
                            <table className="w-full text-xs">
                                <thead className="bg-blue-gray-50">
                                    <tr>
                                        {TARGET_FIELDS.filter(f => mapping[f.key]).map(f => (
                                            <th key={f.key} className="px-3 py-2 text-left font-semibold text-blue-gray-500 whitespace-nowrap">{f.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-blue-gray-50">
                                    {csvData.rows.slice(0, 5).map((row, i) => (
                                        <tr key={i} className="hover:bg-blue-gray-50/50">
                                            {TARGET_FIELDS.filter(f => mapping[f.key]).map(f => (
                                                <td key={f.key} className="px-3 py-2 text-blue-gray-700 truncate max-w-[140px]" title={row[mapping[f.key]] || ''}>
                                                    {row[mapping[f.key]] || <span className="text-blue-gray-300">–</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {!urlMapped && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            La colonne URL est obligatoire. Associez-la à une colonne de votre fichier.
                        </div>
                    )}

                    <div className="flex justify-between gap-3 mt-2">
                        <Button variant="secondary" onClick={() => setStep(1)} type="button">
                            <ChevronLeft size={14} /> Retour
                        </Button>
                        <Button variant="primary" onClick={() => setStep(3)} disabled={!urlMapped} type="button">
                            Continuer <ChevronRight size={14} />
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Étape 3 : Options & Lancement ── */}
            {step === 3 && csvData && !importResult && (
                <div className="flex flex-col gap-5">
                    {/* Résumé */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-gray-50 rounded-xl px-4 py-3 text-center">
                            <div className="text-2xl font-bold text-blue-gray-800">{csvData.rows.length}</div>
                            <div className="text-xs text-blue-gray-500 mt-0.5">Lignes totales</div>
                        </div>
                        <div className="bg-green-50 rounded-xl px-4 py-3 text-center">
                            <div className="text-2xl font-bold text-green-700">{validRows.length}</div>
                            <div className="text-xs text-green-600 mt-0.5">Lignes valides</div>
                        </div>
                        <div className={`rounded-xl px-4 py-3 text-center ${invalidRows > 0 ? 'bg-amber-50' : 'bg-blue-gray-50'}`}>
                            <div className={`text-2xl font-bold ${invalidRows > 0 ? 'text-amber-700' : 'text-blue-gray-400'}`}>{invalidRows}</div>
                            <div className={`text-xs mt-0.5 ${invalidRows > 0 ? 'text-amber-600' : 'text-blue-gray-400'}`}>Sans URL (ignorées)</div>
                        </div>
                    </div>

                    {/* Lancer les audits ? */}
                    <div className="flex flex-col gap-3">
                        <p className="text-sm font-medium text-blue-gray-700">Que faire après l'import ?</p>
                        <div className="flex flex-col gap-2">
                            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${runAudit ? 'border-accent bg-accent/5' : 'border-blue-gray-200 hover:bg-blue-gray-50'}`}>
                                <input type="radio" name="runAudit" className="mt-0.5" checked={runAudit} onChange={() => setRunAudit(true)} />
                                <div>
                                    <div className="text-sm font-medium text-blue-gray-700">Importer et lancer les audits SEO</div>
                                    <div className="text-xs text-blue-gray-500 mt-0.5">Chaque prospect reçoit un audit gratuit automatique. Aucun email ne lui sera envoyé.</div>
                                </div>
                            </label>
                            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${!runAudit ? 'border-accent bg-accent/5' : 'border-blue-gray-200 hover:bg-blue-gray-50'}`}>
                                <input type="radio" name="runAudit" className="mt-0.5" checked={!runAudit} onChange={() => setRunAudit(false)} />
                                <div>
                                    <div className="text-sm font-medium text-blue-gray-700">Importer seulement (sans audit)</div>
                                    <div className="text-xs text-blue-gray-500 mt-0.5">Les prospects sont créés dans la liste. Vous pourrez lancer les audits manuellement.</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Anti-doublon */}
                    <div>
                        <label className="text-sm font-medium text-blue-gray-700 flex items-center gap-2 mb-2">
                            Protection anti-doublon
                            <Tooltip content="Si un domaine a déjà un audit dans la période choisie, la ligne est ignorée." />
                        </label>
                        <select
                            className="px-3 py-2 text-sm border border-blue-gray-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-accent/20"
                            value={dedupDays}
                            onChange={e => setDedupDays(Number(e.target.value))}
                        >
                            <option value={0}>Désactivée (tout importer)</option>
                            <option value={1}>1 jour</option>
                            <option value={7}>7 jours</option>
                            <option value={30}>30 jours</option>
                        </select>
                    </div>

                    {/* UTM de tracking pour cette liste */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-blue-gray-700 mb-2 block">
                                UTM Source
                            </label>
                            <input
                                type="text"
                                placeholder="ex: linkedin_list_mars"
                                value={utmSource}
                                onChange={(e) => setUtmSource(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-blue-gray-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-accent/20"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-blue-gray-700 mb-2 block">
                                UTM Medium
                            </label>
                            <input
                                type="text"
                                placeholder="ex: cold_email"
                                value={utmMedium}
                                onChange={(e) => setUtmMedium(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-blue-gray-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-accent/20"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-blue-gray-400 -mt-1">
                        Les liens d'audit générés pour cette importation incluront automatiquement
                        {` ?utm_source=${utmSource || '...'}&utm_medium=${utmMedium || '...'}`}.
                    </p>

                    <div className="flex justify-between gap-3 mt-2">
                        <Button variant="secondary" onClick={() => setStep(2)} type="button" disabled={importing}>
                            <ChevronLeft size={14} /> Retour
                        </Button>
                        <Button variant="primary" onClick={handleImport} disabled={importing || validRows.length === 0} type="button">
                            {importing ? (
                                <><Loader2 size={14} className="animate-spin" /> Import en cours…</>
                            ) : (
                                <><Check size={14} /> Importer {validRows.length} prospect{validRows.length > 1 ? 's' : ''}</>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Résultat import ── */}
            {step === 3 && importResult && (
                <div className="flex flex-col gap-5">
                    {importResult.error ? (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 border border-red-100">
                            <AlertTriangle size={16} className="flex-shrink-0" />
                            <span><strong>Erreur :</strong> {importResult.error}</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-4 py-3 border border-green-100">
                                <Check size={16} className="flex-shrink-0" />
                                <span>Import terminé — un onglet <strong>"{listName}"</strong> a été créé dans vos prospects.</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <Stat label="Créés" value={importResult.total_created} color="green" />
                                <Stat label="Audits lancés" value={importResult.total_queued} color="blue" />
                                <Stat label="Doublons ignorés" value={importResult.total_duplicate} color="amber" />
                                <Stat label="Invalides" value={importResult.total_invalid} color="red" />
                            </div>
                            {importResult.total_queued > 0 && (
                                <div className="flex items-center gap-2 text-xs text-blue-gray-500 bg-blue-gray-50 rounded-lg px-3 py-2">
                                    <Info size={12} className="flex-shrink-0 text-accent" />
                                    Les audits tournent en arrière-plan. Actualisez la liste dans quelques minutes pour voir les résultats.
                                </div>
                            )}
                        </>
                    )}
                    <div className="flex justify-end gap-3">
                        {importResult.error && (
                            <Button variant="secondary" onClick={() => setImportResult(null)} type="button">
                                <RefreshCw size={14} /> Réessayer
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleClose} type="button">
                            Fermer
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function Stat({ label, value, color }) {
    const colors = {
        green: 'bg-green-50 text-green-700',
        blue: 'bg-blue-50 text-blue-700',
        amber: 'bg-amber-50 text-amber-700',
        red: 'bg-red-50 text-red-700',
    };
    return (
        <div className={`rounded-xl px-4 py-3 text-center ${colors[color] || 'bg-blue-gray-50 text-blue-gray-700'}`}>
            <div className="text-2xl font-bold">{value ?? 0}</div>
            <div className="text-xs mt-0.5 opacity-80">{label}</div>
        </div>
    );
}
